import React, { useState, useRef, Children, lazy, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useBusiness } from './BusinessContext';
import { LeadDetailModal, PIPELINE_STAGES, type Lead, type LeadStatus } from './LeadDetailModal';
import { XIcon, SparklesIcon, MessageSquareIcon, TrendingUpIcon, SettingsIcon, FileTextIcon, DollarSignIcon, BotIcon, CheckCircle2Icon, SendIcon, UserIcon, EditIcon, CheckIcon, EyeIcon, ClockIcon, TrashIcon, ArchiveIcon, ToggleRightIcon, UploadCloudIcon, PlusIcon, ImageIcon, MapPinIcon, ExternalLinkIcon, NavigationIcon, SearchIcon } from 'lucide-react';
import { MapContainer, TileLayer, Marker, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
// Fix Leaflet default icon issue
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png'
});
interface CatalogItemModalProps {
  onClose: () => void;
  itemTitle?: string;
}
type Tab = 'Overview' | 'Pricing' | 'AI Knowledge' | 'Leads' | 'Activity' | 'Settings';
export function CatalogItemModal({
  onClose,
  itemTitle = 'Toyota Prado GXR 2018'
}: CatalogItemModalProps) {
  const [activeTab, setActiveTab] = useState<Tab>('Overview');
  const tabs: {
    id: Tab;
    icon: React.ReactNode;
  }[] = [{
    id: 'Overview',
    icon: <FileTextIcon className="w-4 h-4" data-id="element-1111" />
  }, {
    id: 'Pricing',
    icon: <DollarSignIcon className="w-4 h-4" data-id="element-1112" />
  }, {
    id: 'AI Knowledge',
    icon: <SparklesIcon className="w-4 h-4" data-id="element-1113" />
  }, {
    id: 'Leads',
    icon: <TrendingUpIcon className="w-4 h-4" data-id="element-1114" />
  }, {
    id: 'Activity',
    icon: <TrendingUpIcon className="w-4 h-4" data-id="element-1115" />
  }, {
    id: 'Settings',
    icon: <SettingsIcon className="w-4 h-4" data-id="element-1116" />
  }];
  return <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6" data-id="element-1117">
      <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm" onClick={onClose} data-id="element-1118" />
      <motion.div initial={{
      opacity: 0,
      scale: 0.95,
      y: 20
    }} animate={{
      opacity: 1,
      scale: 1,
      y: 0
    }} exit={{
      opacity: 0,
      scale: 0.95,
      y: 20
    }} className="relative w-full max-w-5xl bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col h-[85vh]" data-id="element-1119">
        {/* Header */}
        <div className="flex-shrink-0 px-6 py-4 border-b border-slate-100 flex items-center justify-between bg-white z-10" data-id="element-1120">
          <div className="flex items-center gap-4" data-id="element-1121">
            <div className="w-12 h-12 bg-slate-100 rounded-lg flex items-center justify-center overflow-hidden" data-id="element-1122">
              <img src="https://images.unsplash.com/photo-1550133730-695473e544be?auto=format&fit=crop&q=80&w=100&h=100" alt="Prado" className="w-full h-full object-cover" data-id="element-1123" />
            </div>
            <div data-id="element-1124">
              <div className="flex items-center gap-2" data-id="element-1125">
                <h2 className="text-xl font-display font-bold text-dark-navy" data-id="element-1126">
                  {itemTitle}
                </h2>
                <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs font-bold rounded-full" data-id="element-1127">
                  Active
                </span>
              </div>
              <div className="flex items-center gap-3 text-sm text-slate-500 mt-1" data-id="element-1128">
                <span className="px-2 py-0.5 bg-slate-100 text-slate-700 text-xs font-medium rounded-md" data-id="element-1129">
                  SUVs
                </span>
                <span data-id="element-1130">•</span>
                <span data-id="element-1131">AED 350/day</span>
                <span data-id="element-1132">•</span>
                <span className="flex items-center gap-1 text-ai-purple font-medium" data-id="element-1133">
                  <SparklesIcon className="w-3 h-3" data-id="element-1134" /> AI Ready: 92%
                </span>
              </div>
            </div>
          </div>
          <button onClick={onClose} className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-50 rounded-full transition-colors" data-id="element-1135">
            <XIcon className="w-5 h-5" data-id="element-1136" />
          </button>
        </div>

        <div className="flex flex-1 overflow-hidden" data-id="element-1137">
          {/* Sidebar Tabs */}
          <div className="w-48 flex-shrink-0 border-r border-slate-100 bg-slate-50/50 p-3 overflow-y-auto" data-id="element-1138">
            <div className="space-y-1" data-id="element-1139">
              {tabs.map(tab => <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`w-full flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${activeTab === tab.id ? 'bg-white text-primary-indigo shadow-sm border border-slate-200' : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'}`} data-id="element-1140">
                  <span className={activeTab === tab.id ? 'text-primary-indigo' : 'text-slate-400'} data-id="element-1141">
                    {tab.icon}
                  </span>
                  {tab.id}
                </button>)}
            </div>
          </div>

          {/* Content Area */}
          <div className="flex-1 overflow-y-auto bg-white p-6" data-id="element-1142">
            {activeTab === 'Overview' && <OverviewTab data-id="element-1143" />}
            {activeTab === 'Pricing' && <PricingTab data-id="element-1144" />}
            {activeTab === 'AI Knowledge' && <AIRulesTab data-id="element-1145" />}
            {activeTab === 'Leads' && <LeadsTab itemTitle={itemTitle} data-id="element-1146" />}
            {activeTab === 'Activity' && <ActivityTab data-id="element-1147" />}
            {activeTab === 'Settings' && <SettingsTab onClose={onClose} data-id="element-1148" />}
          </div>
        </div>
      </motion.div>
    </div>;
}
const UAE_PLACES = [{
  id: '1',
  name: 'Al Quoz Industrial Area 3',
  city: 'Dubai',
  area: 'Al Quoz',
  lat: 25.1275,
  lng: 55.2289
}, {
  id: '2',
  name: 'Dubai Marina Mall',
  city: 'Dubai',
  area: 'Dubai Marina',
  lat: 25.0768,
  lng: 55.1404
}, {
  id: '3',
  name: 'Jumeirah Beach Residence',
  city: 'Dubai',
  area: 'JBR',
  lat: 25.0776,
  lng: 55.1328
}, {
  id: '4',
  name: 'Business Bay',
  city: 'Dubai',
  area: 'Business Bay',
  lat: 25.1846,
  lng: 55.2672
}, {
  id: '5',
  name: 'Downtown Dubai',
  city: 'Dubai',
  area: 'Downtown',
  lat: 25.1972,
  lng: 55.2744
}, {
  id: '6',
  name: 'Al Maryah Island',
  city: 'Abu Dhabi',
  area: 'Al Maryah',
  lat: 24.5019,
  lng: 54.3905
}, {
  id: '7',
  name: 'Yas Island',
  city: 'Abu Dhabi',
  area: 'Yas Island',
  lat: 24.4961,
  lng: 54.6039
}, {
  id: '8',
  name: 'Al Majaz Waterfront',
  city: 'Sharjah',
  area: 'Al Majaz',
  lat: 25.3216,
  lng: 55.3855
}];
function MapEvents({
  onLocationSelect
}: {
  onLocationSelect: (lat: number, lng: number) => void;
}) {
  useMapEvents({
    click(e) {
      onLocationSelect(e.latlng.lat, e.latlng.lng);
    }
  });
  return null;
}
function OverviewTab() {
  const [isEditing, setIsEditing] = useState(false);
  const [images, setImages] = useState(['https://images.unsplash.com/photo-1550133730-695473e544be?auto=format&fit=crop&q=80&w=800', 'https://images.unsplash.com/photo-1550133730-695473e544be?auto=format&fit=crop&q=80&w=800&sig=1', 'https://images.unsplash.com/photo-1550133730-695473e544be?auto=format&fit=crop&q=80&w=800&sig=2', 'https://images.unsplash.com/photo-1550133730-695473e544be?auto=format&fit=crop&q=80&w=800&sig=3']);
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [details, setDetails] = useState({
    title: 'Toyota Prado GXR 2023',
    price: '350',
    status: 'Available',
    description: 'Premium SUV perfect for family trips and desert adventures. Fully insured and maintained. Features include leather seats, sunroof, advanced navigation, and 4WD capabilities.',
    specs: {
      Category: 'SUVs',
      Make: 'Toyota',
      Model: 'Prado GXR',
      Year: '2023',
      Seats: '7',
      Transmission: 'Automatic',
      'Fuel Type': 'Petrol'
    }
  });
  const [location, setLocation] = useState({
    address: 'Al Quoz Industrial Area 3, Dubai, UAE',
    city: 'Dubai',
    area: 'Al Quoz',
    lat: 25.1275,
    lng: 55.2289
  });
  // Edit mode location state
  const [draftLocation, setDraftLocation] = useState(location);
  const [searchQuery, setSearchQuery] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const filteredPlaces = UAE_PLACES.filter(p => p.name.toLowerCase().includes(searchQuery.toLowerCase()) || p.city.toLowerCase().includes(searchQuery.toLowerCase()) || p.area.toLowerCase().includes(searchQuery.toLowerCase()));
  const handlePlaceSelect = (place: (typeof UAE_PLACES)[0]) => {
    setDraftLocation({
      address: `${place.name}, ${place.city}, UAE`,
      city: place.city,
      area: place.area,
      lat: place.lat,
      lng: place.lng
    });
    setSearchQuery(place.name);
    setShowSuggestions(false);
  };
  const handleMapClick = (lat: number, lng: number) => {
    // Resolve the dropped pin to the nearest known UAE place so we can capture
    // a real city + area (lightweight reverse-geocode against the curated set).
    const nearest = UAE_PLACES.reduce((closest, place) => {
      const dist = (place.lat - lat) ** 2 + (place.lng - lng) ** 2;
      const closestDist = (closest.lat - lat) ** 2 + (closest.lng - lng) ** 2;
      return dist < closestDist ? place : closest;
    }, UAE_PLACES[0]);
    setDraftLocation({
      address: `Near ${nearest.area}, ${nearest.city}, UAE`,
      city: nearest.city,
      area: nearest.area,
      lat,
      lng
    });
    setSearchQuery('');
  };
  const saveLocation = () => {
    setLocation(draftLocation);
    // Optional: show a toast here
  };
  const mapQuery = location.address || `${location.area} ${location.city}`;
  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const newImages = [...images, images[0]];
      setImages(newImages);
      setActiveImageIndex(newImages.length - 1);
    }
  };
  const handleDeleteImage = () => {
    if (images.length > 1) {
      const newImages = images.filter((_, idx) => idx !== activeImageIndex);
      setImages(newImages);
      setActiveImageIndex(Math.max(0, activeImageIndex - 1));
    }
  };
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.05
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 10
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        type: 'spring',
        stiffness: 300,
        damping: 24
      }
    }
  };
  return <div className="flex flex-col gap-8 pb-2" data-id="element-1149">
      {/* ===== HERO: Title + Grand Gallery ===== */}
      <motion.div variants={containerVariants} initial="hidden" animate="show" className="flex flex-col gap-5" data-id="element-1150">
        {/* Title row */}
        <motion.div variants={itemVariants} className="flex flex-col sm:flex-row sm:items-end justify-between gap-4" data-id="element-1151">
          <div data-id="element-1152">
            <div className="flex items-center gap-3 mb-2.5" data-id="element-1153">
              <span className="px-2.5 py-1 bg-green-50 border border-green-200 text-green-700 text-xs font-bold uppercase tracking-wider rounded-lg shadow-sm" data-id="element-1154">
                {details.status}
              </span>
              <span className="px-2.5 py-1 bg-slate-100 text-slate-600 text-xs font-medium rounded-lg" data-id="element-1155">
                {details.specs.Category}
              </span>
              <span className="text-xs font-medium text-slate-400 flex items-center gap-1" data-id="element-1156">
                <ClockIcon className="w-3.5 h-3.5" data-id="element-1157" /> Updated 2h ago
              </span>
            </div>
            <h3 className="text-3xl sm:text-4xl font-display font-bold text-dark-navy leading-tight" data-id="element-1158">
              {details.title}
            </h3>
          </div>
          <div className="flex items-center gap-4 shrink-0" data-id="element-1159">
            <div className="flex items-baseline gap-1" data-id="element-1160">
              <span className="text-3xl font-bold text-primary-indigo tracking-tight" data-id="element-1161">
                <span className="text-lg text-primary-indigo/70 font-semibold mr-1" data-id="element-1162">
                  AED
                </span>
                {details.price}
              </span>
              <span className="text-sm font-medium text-slate-500" data-id="element-1163">/day</span>
            </div>
            {!isEditing && <button onClick={() => setIsEditing(true)} className="flex items-center gap-2 px-4 py-2.5 text-sm font-semibold text-slate-700 bg-white border border-slate-200 shadow-sm rounded-xl hover:border-primary-indigo hover:text-primary-indigo hover:shadow transition-all group" data-id="element-1164">
                <EditIcon className="w-4 h-4 text-slate-400 group-hover:text-primary-indigo transition-colors" data-id="element-1165" />
                Edit
              </button>}
          </div>
        </motion.div>

        {/* Grand gallery: large main image + thumbnail rail */}
        <motion.div variants={itemVariants} className="flex flex-col gap-3" data-id="element-1166">
          <div className="aspect-[16/9] w-full max-w-xl max-h-[300px] bg-slate-50 rounded-2xl overflow-hidden relative group shadow-sm border border-slate-200/60" data-id="element-1167">
            <AnimatePresence mode="wait" data-id="element-1168">
              <motion.img key={activeImageIndex} src={images[activeImageIndex]} alt="Main" className="w-full h-full object-cover" initial={{
              opacity: 0,
              scale: 1.02
            }} animate={{
              opacity: 1,
              scale: 1
            }} exit={{
              opacity: 0
            }} transition={{
              duration: 0.2
            }} data-id="element-1169" />
            </AnimatePresence>
            <div className="absolute inset-0 bg-slate-900/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-3 backdrop-blur-[2px]" data-id="element-1170">
              <button className="px-4 py-2 bg-white text-slate-700 text-sm font-medium rounded-lg hover:bg-slate-50 shadow-sm flex items-center gap-2 transition-colors" onClick={() => fileInputRef.current?.click()} data-id="element-1171">
                <UploadCloudIcon className="w-4 h-4" data-id="element-1172" /> Replace
              </button>
              {images.length > 1 && <button className="p-2 bg-white text-red-600 rounded-lg hover:bg-red-50 shadow-sm transition-colors" onClick={handleDeleteImage} title="Delete Photo" data-id="element-1173">
                  <TrashIcon className="w-4 h-4" data-id="element-1174" />
                </button>}
            </div>
          </div>
          <div className="flex gap-3 overflow-x-auto pb-1 no-scrollbar" data-id="element-1175">
            {images.map((img, idx) => <button key={idx} onClick={() => setActiveImageIndex(idx)} className={`w-24 h-20 rounded-xl flex-shrink-0 overflow-hidden border-2 transition-all ${idx === activeImageIndex ? 'border-primary-indigo shadow-sm ring-2 ring-primary-indigo/20 ring-offset-1' : 'border-transparent hover:border-slate-300 opacity-70 hover:opacity-100'}`} data-id="element-1176">
                <img src={img} alt={`Thumb ${idx}`} className="w-full h-full object-cover" data-id="element-1177" />
              </button>)}
            <button className="w-24 h-20 rounded-xl flex-shrink-0 border-2 border-dashed border-slate-300 flex flex-col items-center justify-center text-slate-500 hover:border-primary-indigo hover:text-primary-indigo hover:bg-indigo-50/50 transition-colors bg-slate-50" onClick={() => fileInputRef.current?.click()} data-id="element-1178">
              <PlusIcon className="w-5 h-5 mb-0.5" data-id="element-1179" />
              <span className="text-[10px] font-bold uppercase tracking-wider" data-id="element-1180">
                Add
              </span>
            </button>
          </div>
          <input type="file" ref={fileInputRef} className="hidden" accept="image/*" onChange={handleImageUpload} data-id="element-1181" />
        </motion.div>
      </motion.div>

      {/* ===== FULL-WIDTH DETAILS ===== */}
      {isEditing ? <motion.div initial={{
      opacity: 0,
      y: 10
    }} animate={{
      opacity: 1,
      y: 0
    }} className="flex flex-col" data-id="element-1182">
          <div className="flex items-center justify-between mb-6 pb-4 border-b border-slate-100" data-id="element-1183">
            <h3 className="text-xl font-bold text-dark-navy flex items-center gap-2" data-id="element-1184">
              <EditIcon className="w-5 h-5 text-primary-indigo" data-id="element-1185" />
              Edit Details
            </h3>
            <div className="flex items-center gap-2" data-id="element-1186">
              <button onClick={() => setIsEditing(false)} className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-900 bg-slate-50 hover:bg-slate-100 rounded-xl transition-colors" data-id="element-1187">
                Cancel
              </button>
              <button onClick={() => setIsEditing(false)} className="px-4 py-2 bg-primary-indigo text-white text-sm font-medium rounded-xl hover:bg-indigo-700 transition-all flex items-center gap-2" data-id="element-1188">
                <CheckIcon className="w-4 h-4" data-id="element-1189" /> Save Changes
              </button>
            </div>
          </div>

          <div className="space-y-6" data-id="element-1190">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5" data-id="element-1191">
              <div className="sm:col-span-2" data-id="element-1192">
                <label className="block text-sm font-semibold text-slate-700 mb-1.5" data-id="element-1193">
                  Title
                </label>
                <input type="text" value={details.title} onChange={e => setDetails({
              ...details,
              title: e.target.value
            })} className="w-full px-4 py-2.5 bg-slate-50/50 border border-slate-200 rounded-xl focus:bg-white focus:ring-2 focus:ring-primary-indigo/20 focus:border-primary-indigo outline-none transition-all" data-id="element-1194" />
              </div>
              <div data-id="element-1195">
                <label className="block text-sm font-semibold text-slate-700 mb-1.5" data-id="element-1196">
                  Price (AED/day)
                </label>
                <div className="relative" data-id="element-1197">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 font-medium" data-id="element-1198">
                    AED
                  </span>
                  <input type="text" value={details.price} onChange={e => setDetails({
                ...details,
                price: e.target.value
              })} className="w-full pl-12 pr-4 py-2.5 bg-slate-50/50 border border-slate-200 rounded-xl focus:bg-white focus:ring-2 focus:ring-primary-indigo/20 focus:border-primary-indigo outline-none transition-all" data-id="element-1199" />
                </div>
              </div>
              <div data-id="element-1200">
                <label className="block text-sm font-semibold text-slate-700 mb-1.5" data-id="element-1201">
                  Status
                </label>
                <select value={details.status} onChange={e => setDetails({
              ...details,
              status: e.target.value
            })} className="w-full px-4 py-2.5 bg-slate-50/50 border border-slate-200 rounded-xl focus:bg-white focus:ring-2 focus:ring-primary-indigo/20 focus:border-primary-indigo outline-none transition-all appearance-none" data-id="element-1202">
                  <option value="Available" data-id="element-1203">Available</option>
                  <option value="Rented" data-id="element-1204">Rented</option>
                  <option value="Maintenance" data-id="element-1205">Maintenance</option>
                </select>
              </div>
              <div className="sm:col-span-2" data-id="element-1206">
                <label className="block text-sm font-semibold text-slate-700 mb-1.5" data-id="element-1207">
                  Description
                </label>
                <textarea rows={4} value={details.description} onChange={e => setDetails({
              ...details,
              description: e.target.value
            })} className="w-full px-4 py-3 bg-slate-50/50 border border-slate-200 rounded-xl focus:bg-white focus:ring-2 focus:ring-primary-indigo/20 focus:border-primary-indigo outline-none resize-none transition-all leading-relaxed" data-id="element-1208" />
              </div>
            </div>

            <div data-id="element-1209">
              <div className="flex items-center gap-2 mb-4" data-id="element-1210">
                <div className="h-px flex-1 bg-slate-100" data-id="element-1211" />
                <span className="text-xs font-bold uppercase tracking-wider text-slate-400" data-id="element-1212">
                  Key Specifications
                </span>
                <div className="h-px flex-1 bg-slate-100" data-id="element-1213" />
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4" data-id="element-1214">
                {Object.entries(details.specs).map(([key, val]) => <div key={key} className="bg-white p-3 rounded-xl border border-slate-200 shadow-sm" data-id="element-1215">
                    <label className="block text-xs font-medium text-slate-500 mb-1.5" data-id="element-1216">
                      {key}
                    </label>
                    <input type="text" value={val} onChange={e => setDetails({
                ...details,
                specs: {
                  ...details.specs,
                  [key]: e.target.value
                }
              })} className="w-full px-2.5 py-1.5 bg-slate-50 border border-slate-200 rounded-lg focus:bg-white focus:ring-2 focus:ring-primary-indigo/20 focus:border-primary-indigo outline-none text-sm transition-all" data-id="element-1217" />
                  </div>)}
              </div>
            </div>

            {/* Interactive Product Location Editor */}
            <div data-id="element-1218">
              <div className="flex items-center gap-2 mb-4" data-id="element-1219">
                <div className="h-px flex-1 bg-slate-100" data-id="element-1220" />
                <span className="text-xs font-bold uppercase tracking-wider text-slate-400" data-id="element-1221">
                  Product Location
                </span>
                <div className="h-px flex-1 bg-slate-100" data-id="element-1222" />
              </div>

              <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm" data-id="element-1223">
                <div className="p-4 border-b border-slate-100 bg-slate-50/50" data-id="element-1224">
                  <label className="block text-sm font-semibold text-slate-700 mb-2" data-id="element-1225">
                    Search Location
                  </label>
                  <div className="relative" data-id="element-1226">
                    <SearchIcon className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" data-id="element-1227" />
                    <input value={searchQuery} onChange={e => {
                  setSearchQuery(e.target.value);
                  setShowSuggestions(true);
                }} onFocus={() => setShowSuggestions(true)} placeholder="Search places in UAE..." className="w-full pl-9 pr-4 py-2.5 bg-white border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary-indigo/20 focus:border-primary-indigo outline-none transition-all text-sm" data-id="element-1228" />
                    {showSuggestions && searchQuery && filteredPlaces.length > 0 && <div className="absolute z-20 w-full mt-1 bg-white border border-slate-200 rounded-xl shadow-lg max-h-48 overflow-y-auto" data-id="element-1229">
                          {filteredPlaces.map(place => <button key={place.id} onClick={() => handlePlaceSelect(place)} className="w-full text-left px-4 py-2.5 hover:bg-slate-50 border-b border-slate-50 last:border-0 transition-colors flex items-center gap-3" data-id="element-1230">
                              <MapPinIcon className="w-4 h-4 text-slate-400 shrink-0" data-id="element-1231" />
                              <div data-id="element-1232">
                                <div className="text-sm font-medium text-slate-900" data-id="element-1233">
                                  {place.name}
                                </div>
                                <div className="text-xs text-slate-500" data-id="element-1234">
                                  {place.area}, {place.city}
                                </div>
                              </div>
                            </button>)}
                        </div>}
                  </div>
                </div>

                <div className="h-64 relative z-10" data-id="element-1235">
                  <MapContainer center={[draftLocation.lat, draftLocation.lng]} zoom={13} scrollWheelZoom={false} className="w-full h-full" data-id="element-1236">
                    <TileLayer attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" data-id="element-1237" />
                    <Marker position={[draftLocation.lat, draftLocation.lng]} data-id="element-1238" />
                    <MapEvents onLocationSelect={handleMapClick} data-id="element-1239" />
                  </MapContainer>
                </div>

                <div className="p-4 bg-slate-50/50 border-t border-slate-100 flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-id="element-1240">
                  <div data-id="element-1241">
                    <div className="text-sm font-semibold text-slate-900" data-id="element-1242">
                      {draftLocation.address}
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5" data-id="element-1243">
                      Coordinates: {draftLocation.lat.toFixed(4)},{' '}
                      {draftLocation.lng.toFixed(4)}
                    </div>
                  </div>
                  <button onClick={saveLocation} className="px-4 py-2 bg-primary-indigo text-white text-sm font-semibold rounded-xl hover:bg-indigo-700 transition-colors flex items-center gap-2 shrink-0 shadow-sm" data-id="element-1244">
                    <CheckIcon className="w-4 h-4" data-id="element-1245" /> Save Location
                  </button>
                </div>
              </div>
            </div>
          </div>
        </motion.div> : <motion.div variants={containerVariants} initial="hidden" animate="show" className="flex flex-col gap-8" data-id="element-1246">
          {/* AI Readiness Card (full width) */}
          <motion.div variants={itemVariants} data-id="element-1247">
            <div className="relative overflow-hidden bg-purple-50 border border-purple-100 rounded-2xl p-5 shadow-sm" data-id="element-1248">
              <div className="relative flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-id="element-1249">
                <div className="flex items-center gap-3" data-id="element-1250">
                  <div className="w-10 h-10 rounded-xl bg-white shadow-sm flex items-center justify-center shrink-0" data-id="element-1251">
                    <SparklesIcon className="w-5 h-5 text-ai-purple" data-id="element-1252" />
                  </div>
                  <div data-id="element-1253">
                    <h4 className="font-bold text-slate-900 text-sm" data-id="element-1254">
                      AI Readiness Score
                    </h4>
                    <p className="text-xs text-slate-500 mt-0.5" data-id="element-1255">
                      Optimized for AI matching
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3" data-id="element-1256">
                  <div className="w-40 h-2.5 bg-white rounded-full overflow-hidden shadow-inner" data-id="element-1257">
                    <motion.div initial={{
                  width: 0
                }} animate={{
                  width: '92%'
                }} transition={{
                  duration: 1,
                  delay: 0.2,
                  ease: 'easeOut'
                }} className="h-full bg-ai-purple rounded-full" data-id="element-1258" />
                  </div>
                  <span className="font-bold text-ai-purple text-lg" data-id="element-1259">92%</span>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Description (full width) */}
          <motion.div variants={itemVariants} data-id="element-1260">
            <h4 className="text-sm font-bold text-slate-900 mb-3 flex items-center gap-2" data-id="element-1261">
              <FileTextIcon className="w-4 h-4 text-slate-400" data-id="element-1262" /> Description
            </h4>
            <div className="bg-slate-50/50 border border-slate-100 rounded-2xl p-5" data-id="element-1263">
              <p className="text-sm text-slate-600 leading-relaxed max-w-3xl" data-id="element-1264">
                {details.description}
              </p>
            </div>
          </motion.div>

          {/* Key Specs Grid (full width) */}
          <motion.div variants={itemVariants} data-id="element-1265">
            <h4 className="text-sm font-bold text-slate-900 mb-3 flex items-center gap-2" data-id="element-1266">
              <SettingsIcon className="w-4 h-4 text-slate-400" data-id="element-1267" /> Key
              Specifications
            </h4>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-id="element-1268">
              {Object.entries(details.specs).map(([key, val]) => <div key={key} className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm hover:shadow-md hover:border-slate-300 transition-all group" data-id="element-1269">
                  <span className="block text-xs font-medium text-slate-500 mb-1 group-hover:text-primary-indigo transition-colors" data-id="element-1270">
                    {key}
                  </span>
                  <span className="block font-semibold text-slate-900 text-sm truncate" title={val} data-id="element-1271">
                    {val}
                  </span>
                </div>)}
            </div>
          </motion.div>

          {/* Product Location (full width) */}
          <motion.div variants={itemVariants} data-id="element-1272">
            <h4 className="text-sm font-bold text-slate-900 mb-3 flex items-center gap-2" data-id="element-1273">
              <MapPinIcon className="w-4 h-4 text-slate-400" data-id="element-1274" /> Location
            </h4>
            <div className="bg-slate-50/50 border border-slate-100 rounded-2xl p-5 flex flex-col gap-4" data-id="element-1275">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3" data-id="element-1276">
                <div className="flex items-start gap-3" data-id="element-1277">
                  <div className="w-10 h-10 rounded-xl bg-white border border-slate-200 shadow-sm flex items-center justify-center shrink-0" data-id="element-1278">
                    <MapPinIcon className="w-5 h-5 text-primary-indigo" data-id="element-1279" />
                  </div>
                  <div data-id="element-1280">
                    <p className="text-sm font-semibold text-slate-900" data-id="element-1281">
                      {location.area}, {location.city}
                    </p>
                    <p className="text-xs text-slate-500 mt-0.5 max-w-md" data-id="element-1282">
                      {location.address}
                    </p>
                    <div className="flex items-center gap-2 mt-2" data-id="element-1283">
                      <span className="px-2.5 py-1 bg-white border border-slate-200 text-slate-600 text-xs font-medium rounded-lg" data-id="element-1284">
                        City: {location.city}
                      </span>
                      <span className="px-2.5 py-1 bg-white border border-slate-200 text-slate-600 text-xs font-medium rounded-lg" data-id="element-1285">
                        Area: {location.area}
                      </span>
                    </div>
                  </div>
                </div>
                <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(mapQuery)}`} target="_blank" rel="noreferrer" className="px-4 py-2.5 bg-white border border-slate-200 text-slate-700 text-sm font-medium rounded-xl hover:border-primary-indigo hover:text-primary-indigo transition-colors flex items-center justify-center gap-2 whitespace-nowrap shrink-0" data-id="element-1286">
                  <ExternalLinkIcon className="w-4 h-4" data-id="element-1287" /> Open in Maps
                </a>
              </div>
              <div className="rounded-xl overflow-hidden border border-slate-200 shadow-sm h-56 relative z-10" data-id="element-1288">
                <MapContainer center={[location.lat, location.lng]} zoom={14} scrollWheelZoom={false} className="w-full h-full" data-id="element-1289">
                  <TileLayer attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" data-id="element-1290" />
                  <Marker position={[location.lat, location.lng]} data-id="element-1291" />
                </MapContainer>
              </div>
            </div>
          </motion.div>
        </motion.div>}
    </div>;
}
interface DeliveryRow {
  id: number;
  location: string;
  charge: string;
}
interface AddonRow {
  id: number;
  item: string;
  type: string;
  amount: string;
}
function PricingTab() {
  const [isEditing, setIsEditing] = useState(false);
  const [base, setBase] = useState({
    daily: '350',
    weekly: '2100',
    monthly: '7500'
  });
  const [delivery, setDelivery] = useState<DeliveryRow[]>([{
    id: 1,
    location: 'Inside Dubai',
    charge: '50'
  }, {
    id: 2,
    location: 'Sharjah',
    charge: '100'
  }, {
    id: 3,
    location: 'Airport Delivery',
    charge: '75'
  }]);
  const [addons, setAddons] = useState<AddonRow[]>([{
    id: 1,
    item: 'Security Deposit',
    type: 'Refundable',
    amount: '1000'
  }, {
    id: 2,
    item: 'Additional Driver',
    type: 'Per Day',
    amount: '25'
  }, {
    id: 3,
    item: 'Child Seat',
    type: 'Fixed',
    amount: '50'
  }]);
  const num = (v: string) => parseFloat(v.replace(/,/g, '')) || 0;
  const fmt = (v: number) => v.toLocaleString('en-US');
  // Auto-computed savings vs. paying the daily rate for the whole period.
  const weeklyDiscount = Math.max(0, Math.round((1 - num(base.weekly) / (num(base.daily) * 7)) * 100));
  const monthlyDiscount = Math.max(0, Math.round((1 - num(base.monthly) / (num(base.daily) * 30)) * 100));
  const updateDelivery = (id: number, field: keyof DeliveryRow, val: string) => setDelivery(prev => prev.map(r => r.id === id ? {
    ...r,
    [field]: val
  } : r));
  const addDelivery = () => setDelivery(prev => [...prev, {
    id: Date.now(),
    location: 'New location',
    charge: '0'
  }]);
  const removeDelivery = (id: number) => setDelivery(prev => prev.filter(r => r.id !== id));
  const updateAddon = (id: number, field: keyof AddonRow, val: string) => setAddons(prev => prev.map(r => r.id === id ? {
    ...r,
    [field]: val
  } : r));
  const addAddon = () => setAddons(prev => [...prev, {
    id: Date.now(),
    item: 'New add-on',
    type: 'Fixed',
    amount: '0'
  }]);
  const removeAddon = (id: number) => setAddons(prev => prev.filter(r => r.id !== id));
  const baseCards = [{
    key: 'daily' as const,
    label: 'Daily Rate',
    discount: 0
  }, {
    key: 'weekly' as const,
    label: 'Weekly Rate',
    discount: weeklyDiscount
  }, {
    key: 'monthly' as const,
    label: 'Monthly Rate',
    discount: monthlyDiscount
  }];
  return <div className="max-w-3xl space-y-8" data-id="element-1292">
      {/* Header */}
      <div className="flex items-center justify-between" data-id="element-1293">
        <div data-id="element-1294">
          <h3 className="text-xl font-bold text-dark-navy flex items-center gap-2" data-id="element-1295">
            <DollarSignIcon className="w-5 h-5 text-primary-indigo" data-id="element-1296" /> Pricing
          </h3>
          <p className="text-sm text-slate-500 mt-1" data-id="element-1297">
            Set the rates, charges and add-ons the AI quotes to customers.
          </p>
        </div>
        {isEditing ? <div className="flex items-center gap-2" data-id="element-1298">
            <button onClick={() => setIsEditing(false)} className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-900 bg-slate-50 hover:bg-slate-100 rounded-xl transition-colors" data-id="element-1299">
              Cancel
            </button>
            <button onClick={() => setIsEditing(false)} className="px-4 py-2 bg-primary-indigo text-white text-sm font-medium rounded-xl hover:bg-indigo-700 transition-colors flex items-center gap-2" data-id="element-1300">
              <CheckIcon className="w-4 h-4" data-id="element-1301" /> Save Changes
            </button>
          </div> : <button onClick={() => setIsEditing(true)} className="flex items-center gap-2 px-4 py-2.5 text-sm font-semibold text-slate-700 bg-white border border-slate-200 shadow-sm rounded-xl hover:border-primary-indigo hover:text-primary-indigo hover:shadow transition-all group" data-id="element-1302">
            <EditIcon className="w-4 h-4 text-slate-400 group-hover:text-primary-indigo transition-colors" data-id="element-1303" />
            Edit Pricing
          </button>}
      </div>

      {/* Base Pricing */}
      <section data-id="element-1304">
        <h4 className="text-sm font-bold text-slate-900 mb-3" data-id="element-1305">Base Pricing</h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-1306">
          {baseCards.map(card => <div key={card.key} className="p-5 bg-white rounded-2xl border border-slate-200 shadow-sm" data-id="element-1307">
              <div className="flex items-center justify-between mb-2" data-id="element-1308">
                <div className="text-sm text-slate-500" data-id="element-1309">{card.label}</div>
                {card.discount > 0 && <span className="text-xs font-bold text-green-700 bg-green-50 border border-green-200 px-2 py-0.5 rounded-md" data-id="element-1310">
                    Save {card.discount}%
                  </span>}
              </div>
              {isEditing ? <div className="relative" data-id="element-1311">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm font-medium" data-id="element-1312">
                    AED
                  </span>
                  <input type="text" value={base[card.key]} onChange={e => setBase({
              ...base,
              [card.key]: e.target.value
            })} className="w-full pl-12 pr-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-lg font-bold text-dark-navy focus:bg-white focus:ring-2 focus:ring-primary-indigo/20 focus:border-primary-indigo outline-none transition-all" data-id="element-1313" />
                </div> : <div className="text-xl font-bold text-dark-navy" data-id="element-1314">
                  AED {fmt(num(base[card.key]))}
                </div>}
            </div>)}
        </div>
        {isEditing && <p className="text-xs text-slate-400 mt-2" data-id="element-1315">
            Weekly and monthly savings are calculated automatically against the
            daily rate.
          </p>}
      </section>

      {/* Delivery & Pickup */}
      <section data-id="element-1316">
        <div className="flex items-center justify-between mb-3" data-id="element-1317">
          <h4 className="text-sm font-bold text-slate-900" data-id="element-1318">
            Delivery &amp; Pickup Charges
          </h4>
          {isEditing && <button onClick={addDelivery} className="text-xs font-semibold text-primary-indigo hover:text-indigo-700 flex items-center gap-1 transition-colors" data-id="element-1319">
              <PlusIcon className="w-3.5 h-3.5" data-id="element-1320" /> Add location
            </button>}
        </div>
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-id="element-1321">
          <table className="w-full text-left text-sm" data-id="element-1322">
            <thead className="bg-slate-50 border-b border-slate-200" data-id="element-1323">
              <tr data-id="element-1324">
                <th className="px-4 py-3 font-medium text-slate-600" data-id="element-1325">
                  Location / Service
                </th>
                <th className="px-4 py-3 font-medium text-slate-600" data-id="element-1326">Charge</th>
                {isEditing && <th className="px-4 py-3 w-10" data-id="element-1327" />}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100" data-id="element-1328">
              {delivery.map(row => <tr key={row.id} data-id="element-1329">
                  <td className="px-4 py-2.5 text-dark-navy" data-id="element-1330">
                    {isEditing ? <input type="text" value={row.location} onChange={e => updateDelivery(row.id, 'location', e.target.value)} className="w-full px-2.5 py-1.5 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:bg-white focus:ring-2 focus:ring-primary-indigo/20 focus:border-primary-indigo outline-none transition-all" data-id="element-1331" /> : row.location}
                  </td>
                  <td className="px-4 py-2.5 font-medium" data-id="element-1332">
                    {isEditing ? <div className="relative w-32" data-id="element-1333">
                        <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 text-xs" data-id="element-1334">
                          AED
                        </span>
                        <input type="text" value={row.charge} onChange={e => updateDelivery(row.id, 'charge', e.target.value)} className="w-full pl-10 pr-2 py-1.5 bg-slate-50 border border-slate-200 rounded-lg text-sm font-medium focus:bg-white focus:ring-2 focus:ring-primary-indigo/20 focus:border-primary-indigo outline-none transition-all" data-id="element-1335" />
                      </div> : `AED ${fmt(num(row.charge))}`}
                  </td>
                  {isEditing && <td className="px-4 py-2.5 text-right" data-id="element-1336">
                      <button onClick={() => removeDelivery(row.id)} className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors" title="Remove" data-id="element-1337">
                        <TrashIcon className="w-4 h-4" data-id="element-1338" />
                      </button>
                    </td>}
                </tr>)}
            </tbody>
          </table>
        </div>
      </section>

      {/* Add-ons & Deposits */}
      <section data-id="element-1339">
        <div className="flex items-center justify-between mb-3" data-id="element-1340">
          <h4 className="text-sm font-bold text-slate-900" data-id="element-1341">
            Add-ons &amp; Deposits
          </h4>
          {isEditing && <button onClick={addAddon} className="text-xs font-semibold text-primary-indigo hover:text-indigo-700 flex items-center gap-1 transition-colors" data-id="element-1342">
              <PlusIcon className="w-3.5 h-3.5" data-id="element-1343" /> Add item
            </button>}
        </div>
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-id="element-1344">
          <table className="w-full text-left text-sm" data-id="element-1345">
            <thead className="bg-slate-50 border-b border-slate-200" data-id="element-1346">
              <tr data-id="element-1347">
                <th className="px-4 py-3 font-medium text-slate-600" data-id="element-1348">Item</th>
                <th className="px-4 py-3 font-medium text-slate-600" data-id="element-1349">Type</th>
                <th className="px-4 py-3 font-medium text-slate-600" data-id="element-1350">Amount</th>
                {isEditing && <th className="px-4 py-3 w-10" data-id="element-1351" />}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100" data-id="element-1352">
              {addons.map(row => <tr key={row.id} data-id="element-1353">
                  <td className="px-4 py-2.5 text-dark-navy" data-id="element-1354">
                    {isEditing ? <input type="text" value={row.item} onChange={e => updateAddon(row.id, 'item', e.target.value)} className="w-full px-2.5 py-1.5 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:bg-white focus:ring-2 focus:ring-primary-indigo/20 focus:border-primary-indigo outline-none transition-all" data-id="element-1355" /> : row.item}
                  </td>
                  <td className="px-4 py-2.5 text-slate-500" data-id="element-1356">
                    {isEditing ? <select value={row.type} onChange={e => updateAddon(row.id, 'type', e.target.value)} className="w-full px-2.5 py-1.5 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:bg-white focus:ring-2 focus:ring-primary-indigo/20 focus:border-primary-indigo outline-none transition-all appearance-none" data-id="element-1357">
                        <option value="Refundable" data-id="element-1358">Refundable</option>
                        <option value="Per Day" data-id="element-1359">Per Day</option>
                        <option value="Fixed" data-id="element-1360">Fixed</option>
                      </select> : row.type}
                  </td>
                  <td className="px-4 py-2.5 font-medium" data-id="element-1361">
                    {isEditing ? <div className="relative w-32" data-id="element-1362">
                        <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 text-xs" data-id="element-1363">
                          AED
                        </span>
                        <input type="text" value={row.amount} onChange={e => updateAddon(row.id, 'amount', e.target.value)} className="w-full pl-10 pr-2 py-1.5 bg-slate-50 border border-slate-200 rounded-lg text-sm font-medium focus:bg-white focus:ring-2 focus:ring-primary-indigo/20 focus:border-primary-indigo outline-none transition-all" data-id="element-1364" />
                      </div> : `AED ${fmt(num(row.amount))}`}
                  </td>
                  {isEditing && <td className="px-4 py-2.5 text-right" data-id="element-1365">
                      <button onClick={() => removeAddon(row.id)} className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors" title="Remove" data-id="element-1366">
                        <TrashIcon className="w-4 h-4" data-id="element-1367" />
                      </button>
                    </td>}
                </tr>)}
            </tbody>
          </table>
        </div>
      </section>
    </div>;
}
// Status pill colors, aligned with the leads pipeline used in the Leads section.
function leadStatusBadge(status: LeadStatus) {
  switch (status) {
    case 'Won':
    case 'Ready To Buy':
      return 'bg-green-100 text-green-700';
    case 'Qualified':
      return 'bg-indigo-100 text-primary-indigo';
    case 'Negotiating':
      return 'bg-blue-100 text-blue-700';
    case 'Interested':
      return 'bg-amber-100 text-amber-700';
    case 'Lost':
      return 'bg-red-100 text-red-700';
    default:
      return 'bg-slate-100 text-slate-600';
  }
}
function LeadsTab({
  itemTitle
}: {
  itemTitle: string;
}) {
  // Leads MAPPED to this specific item (matchedItem === itemTitle), using the
  // same Lead shape + pipeline stages as the main Leads section so they stay
  // consistent and open the same detail view.
  const [leads, setLeads] = useState<Lead[]>([{
    name: 'Ahmed Al Mansoori',
    intentScore: 94,
    lookingFor: 'Toyota Prado',
    budget: 'AED 90,000',
    timeline: 'Within 7 Days',
    location: 'Dubai',
    matchedItem: itemTitle,
    status: 'Qualified',
    summary: 'Customer strongly prefers GCC vehicles. Ready to purchase this week.',
    phone: '+971 50 123 4567',
    email: 'ahmed@email.com',
    preferredContact: 'WhatsApp',
    preferredLanguage: 'Arabic',
    source: 'Talab AI',
    lastActive: '12 minutes ago',
    receivedDate: 'Jun 6, 2026',
    owner: 'Sara',
    nextAction: 'Call customer',
    nextActionDue: 'Today',
    consent: 'shared'
  }, {
    name: 'Omar Haddad',
    intentScore: 67,
    lookingFor: 'Family SUV',
    budget: 'AED 70,000 - 85,000',
    timeline: 'This Month',
    location: 'Sharjah',
    matchedItem: itemTitle,
    status: 'Interested',
    summary: 'Exploring options for a family vehicle. Requested availability confirmation.',
    source: 'Talab AI',
    lastActive: '3 hours ago',
    receivedDate: 'Jun 5, 2026',
    owner: 'Unassigned',
    nextAction: 'Follow up tomorrow',
    nextActionDue: 'Tomorrow',
    consent: 'requested'
  }, {
    name: 'Layla Hassan',
    intentScore: 88,
    lookingFor: 'Toyota Prado GXR',
    budget: 'AED 95,000',
    timeline: 'Within 2 Weeks',
    location: 'Dubai',
    matchedItem: itemTitle,
    status: 'Negotiating',
    summary: 'Asked about delivery to Dubai Marina and the security deposit. Strong engagement.',
    phone: '+971 52 444 1212',
    email: 'layla.h@email.com',
    preferredContact: 'Phone Call',
    preferredLanguage: 'English',
    source: 'Talab AI',
    lastActive: '1 hour ago',
    receivedDate: 'Jun 6, 2026',
    owner: 'Fadi',
    nextAction: 'Send quotation',
    nextActionDue: 'Today',
    consent: 'shared'
  }, {
    name: 'Anonymous Buyer',
    intentScore: 41,
    lookingFor: 'Compact SUV',
    budget: 'AED 60,000',
    timeline: 'Undecided',
    location: 'Dubai',
    matchedItem: itemTitle,
    status: 'Discovered',
    summary: 'Early-stage research. Asked a single pricing question.',
    source: 'Talab AI',
    lastActive: '2 days ago',
    receivedDate: 'Jun 4, 2026',
    owner: 'Unassigned',
    consent: 'not_shared'
  }]);
  const [statusFilter, setStatusFilter] = useState<'All' | LeadStatus>('All');
  const [selectedLead, setSelectedLead] = useState<Lead | null>(null);
  const filtered = statusFilter === 'All' ? leads : leads.filter(l => l.status === statusFilter);
  const updateStatus = (status: LeadStatus) => setSelectedLead(cur => {
    if (!cur) return cur;
    setLeads(prev => prev.map(l => l.name === cur.name ? {
      ...l,
      status
    } : l));
    return {
      ...cur,
      status
    };
  });
  const updateOwner = (owner: string) => setSelectedLead(cur => {
    if (!cur) return cur;
    setLeads(prev => prev.map(l => l.name === cur.name ? {
      ...l,
      owner
    } : l));
    return {
      ...cur,
      owner
    };
  });
  return <div data-id="element-1368">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5" data-id="element-1369">
        <div data-id="element-1370">
          <h3 className="text-lg font-bold text-dark-navy" data-id="element-1371">Leads</h3>
          <p className="text-sm text-slate-500 mt-0.5" data-id="element-1372">
            {leads.length} buyers matched to this item.
          </p>
        </div>
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value as 'All' | LeadStatus)} className="px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-indigo/20" data-id="element-1373">
          <option value="All" data-id="element-1374">All Stages</option>
          {PIPELINE_STAGES.map(s => <option key={s} value={s} data-id="element-1375">
              {s}
            </option>)}
        </select>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-id="element-1376">
        <table className="w-full text-left text-sm" data-id="element-1377">
          <thead className="bg-slate-50 border-b border-slate-200" data-id="element-1378">
            <tr data-id="element-1379">
              <th className="px-4 py-3 font-medium text-slate-600" data-id="element-1380">Customer</th>
              <th className="px-4 py-3 font-medium text-slate-600" data-id="element-1381">Intent</th>
              <th className="px-4 py-3 font-medium text-slate-600" data-id="element-1382">Stage</th>
              <th className="px-4 py-3 font-medium text-slate-600" data-id="element-1383">
                Looking for
              </th>
              <th className="px-4 py-3 font-medium text-slate-600" data-id="element-1384">
                Last active
              </th>
              <th className="px-4 py-3 font-medium text-slate-600 text-right" data-id="element-1385">
                Action
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100" data-id="element-1386">
            {filtered.map((lead, i) => <tr key={i} className="hover:bg-slate-50 transition-colors cursor-pointer" onClick={() => setSelectedLead(lead)} data-id="element-1387">
                <td className="px-4 py-3 font-medium text-dark-navy" data-id="element-1388">
                  <div className="flex items-center gap-2" data-id="element-1389">
                    <div className="w-7 h-7 rounded-full bg-slate-200 flex items-center justify-center text-xs font-bold text-slate-600" data-id="element-1390">
                      {lead.name.charAt(0)}
                    </div>
                    {lead.name}
                  </div>
                </td>
                <td className="px-4 py-3" data-id="element-1391">
                  <span className={`font-bold ${lead.intentScore >= 80 ? 'text-green-600' : lead.intentScore >= 50 ? 'text-amber-600' : 'text-slate-500'}`} data-id="element-1392">
                    {lead.intentScore}
                  </span>
                </td>
                <td className="px-4 py-3" data-id="element-1393">
                  <span className={`px-2 py-1 text-xs font-bold rounded-full ${leadStatusBadge(lead.status)}`} data-id="element-1394">
                    {lead.status}
                  </span>
                </td>
                <td className="px-4 py-3 text-slate-600 truncate max-w-[160px]" data-id="element-1395">
                  {lead.lookingFor}
                </td>
                <td className="px-4 py-3 text-slate-500" data-id="element-1396">
                  {lead.lastActive ?? '—'}
                </td>
                <td className="px-4 py-3 text-right" data-id="element-1397">
                  <button onClick={e => {
                e.stopPropagation();
                setSelectedLead(lead);
              }} className="text-primary-indigo font-medium hover:underline" data-id="element-1398">
                    View Lead
                  </button>
                </td>
              </tr>)}
            {filtered.length === 0 && <tr data-id="element-1399">
                <td colSpan={6} className="px-4 py-12 text-center text-sm text-slate-400" data-id="element-1400">
                  No leads in this stage yet.
                </td>
              </tr>}
          </tbody>
        </table>
      </div>

      {selectedLead && <LeadDetailModal lead={selectedLead} onClose={() => setSelectedLead(null)} onStatusChange={updateStatus} onOwnerChange={updateOwner} data-id="element-1401" />}
    </div>;
}
function ActivityTab() {
  return <div className="max-w-2xl" data-id="element-1402">
      <h3 className="text-lg font-bold text-dark-navy mb-6" data-id="element-1403">Item Activity</h3>

      <div className="relative border-l-2 border-slate-100 ml-3 space-y-8 pb-4" data-id="element-1404">
        <div className="relative pl-6" data-id="element-1405">
          <div className="absolute -left-[9px] top-1 w-4 h-4 rounded-full bg-blue-100 border-2 border-white flex items-center justify-center" data-id="element-1406">
            <div className="w-2 h-2 rounded-full bg-blue-500" data-id="element-1407" />
          </div>
          <div className="text-sm text-slate-500 mb-1" data-id="element-1408">Today, 2:45 PM</div>
          <div className="font-medium text-dark-navy" data-id="element-1409">Price updated</div>
          <div className="text-sm text-slate-600 mt-1" data-id="element-1410">
            Base price changed from AED 320 to AED 350.
          </div>
        </div>

        <div className="relative pl-6" data-id="element-1411">
          <div className="absolute -left-[9px] top-1 w-4 h-4 rounded-full bg-purple-100 border-2 border-white flex items-center justify-center" data-id="element-1412">
            <div className="w-2 h-2 rounded-full bg-ai-purple" data-id="element-1413" />
          </div>
          <div className="text-sm text-slate-500 mb-1" data-id="element-1414">Yesterday, 11:20 AM</div>
          <div className="font-medium text-dark-navy" data-id="element-1415">AI Rule added</div>
          <div className="text-sm text-slate-600 mt-1" data-id="element-1416">
            Added rule: "Airport delivery AED 75".
          </div>
        </div>

        <div className="relative pl-6" data-id="element-1417">
          <div className="absolute -left-[9px] top-1 w-4 h-4 rounded-full bg-green-100 border-2 border-white flex items-center justify-center" data-id="element-1418">
            <div className="w-2 h-2 rounded-full bg-green-500" data-id="element-1419" />
          </div>
          <div className="text-sm text-slate-500 mb-1" data-id="element-1420">Oct 12, 09:00 AM</div>
          <div className="font-medium text-dark-navy" data-id="element-1421">Item published</div>
          <div className="text-sm text-slate-600 mt-1" data-id="element-1422">
            Item moved from Draft to Active.
          </div>
        </div>

        <div className="relative pl-6" data-id="element-1423">
          <div className="absolute -left-[9px] top-1 w-4 h-4 rounded-full bg-slate-200 border-2 border-white flex items-center justify-center" data-id="element-1424">
            <div className="w-2 h-2 rounded-full bg-slate-400" data-id="element-1425" />
          </div>
          <div className="text-sm text-slate-500 mb-1" data-id="element-1426">Oct 10, 04:15 PM</div>
          <div className="font-medium text-dark-navy" data-id="element-1427">Item created</div>
          <div className="text-sm text-slate-600 mt-1" data-id="element-1428">
            Imported via Spreadsheet upload.
          </div>
        </div>
      </div>
    </div>;
}
function SettingsTab({
  onClose
}: {
  onClose: () => void;
}) {
  const {
    trialActive,
    startTrial
  } = useBusiness();
  const VALIDITY_TOTAL = 30;
  const [published, setPublished] = useState(true);
  const [hideFromSearch, setHideFromSearch] = useState(false);
  const [archived, setArchived] = useState(false);
  const [daysRemaining, setDaysRemaining] = useState(12);
  const [showUnpublish, setShowUnpublish] = useState(false);
  const [unpublishReason, setUnpublishReason] = useState('sold');
  const [otherReason, setOtherReason] = useState('');
  const [showTrialPrompt, setShowTrialPrompt] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [itemCategory, setItemCategory] = useState('SUVs');
  const [flash, setFlash] = useState<string | null>(null);
  const notify = (msg: string) => {
    setFlash(msg);
    window.setTimeout(() => setFlash(null), 2500);
  };
  const handlePublishToggle = () => {
    if (published) {
      // Reveal the unpublish reason flow rather than unpublishing instantly.
      setShowUnpublish(v => !v);
    } else {
      // Re-publishing requires an active trial.
      if (!trialActive) {
        setShowTrialPrompt(true);
      } else {
        setPublished(true);
        notify('Item published — now visible to customers.');
      }
    }
  };
  const confirmUnpublish = () => {
    setPublished(false);
    setShowUnpublish(false);
    notify('Item unpublished.');
  };
  const extendValidity = () => {
    setDaysRemaining(d => Math.min(VALIDITY_TOTAL, d + 30));
    notify('Validity extended by 30 days.');
  };
  const toggleHide = () => {
    setHideFromSearch(v => !v);
    notify(hideFromSearch ? 'Item is now searchable.' : 'Item hidden from search.');
  };
  const toggleArchive = () => {
    const next = !archived;
    setArchived(next);
    if (next) setPublished(false);
    notify(next ? 'Item archived.' : 'Item restored to active catalog.');
  };
  const validityPct = Math.round(daysRemaining / VALIDITY_TOTAL * 100);
  return <div className="max-w-2xl space-y-8" data-id="element-1429">
      {/* Flash feedback */}
      <AnimatePresence data-id="element-1430">
        {flash && <motion.div initial={{
        opacity: 0,
        y: -8
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -8
      }} className="sticky top-0 z-20 flex items-center gap-2 px-4 py-2.5 bg-green-50 border border-green-200 text-green-800 text-sm font-medium rounded-xl shadow-sm" data-id="element-1431">
            <CheckCircle2Icon className="w-4 h-4 text-green-600" data-id="element-1432" />
            {flash}
          </motion.div>}
      </AnimatePresence>

      <section data-id="element-1433">
        <h3 className="text-lg font-bold text-dark-navy mb-4" data-id="element-1434">
          Classification
        </h3>
        <div className="bg-white border border-slate-200 rounded-xl p-5" data-id="element-1435">
          <label className="block text-sm font-medium text-slate-700 mb-2" data-id="element-1436">
            Category
          </label>
          <select value={itemCategory} onChange={e => {
          setItemCategory(e.target.value);
          notify(`Category set to ${e.target.value}.`);
        }} className="w-full px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-lg focus:ring-2 focus:ring-primary-indigo/20 text-sm outline-none" data-id="element-1437">
            <option value="SUVs" data-id="element-1438">SUVs</option>
            <option value="Sedans" data-id="element-1439">Sedans</option>
            <option value="Hatchbacks" data-id="element-1440">Hatchbacks</option>
            <option value="Luxury" data-id="element-1441">Luxury</option>
            <option value="Sports" data-id="element-1442">Sports</option>
          </select>
          <p className="text-xs text-slate-500 mt-2" data-id="element-1443">
            Categorizing your item helps customers find it faster when browsing
            or chatting with AI.
          </p>
        </div>
      </section>

      <section data-id="element-1444">
        <h3 className="text-lg font-bold text-dark-navy mb-4" data-id="element-1445">
          Listing Validity
        </h3>
        <div className="bg-white border border-slate-200 rounded-xl p-5" data-id="element-1446">
          <div className="flex items-center justify-between mb-2" data-id="element-1447">
            <div className="font-medium text-dark-navy" data-id="element-1448">Days Remaining</div>
            <div className="font-bold text-primary-indigo" data-id="element-1449">
              {daysRemaining} days
            </div>
          </div>
          <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden mb-3" data-id="element-1450">
            <motion.div className="h-full bg-primary-indigo" animate={{
            width: `${validityPct}%`
          }} transition={{
            type: 'spring',
            stiffness: 200,
            damping: 26
          }} data-id="element-1451" />
          </div>
          <p className="text-sm text-slate-500" data-id="element-1452">
            This listing is valid for {VALIDITY_TOTAL} days.
          </p>
          <button onClick={extendValidity} disabled={daysRemaining >= VALIDITY_TOTAL} className="mt-4 px-4 py-2 bg-slate-50 border border-slate-200 text-slate-700 text-sm font-medium rounded-lg hover:bg-slate-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-1453">
            {daysRemaining >= VALIDITY_TOTAL ? 'Validity at maximum' : 'Extend Validity (+30 days)'}
          </button>
        </div>
      </section>

      <section data-id="element-1454">
        <h3 className="text-lg font-bold text-dark-navy mb-4" data-id="element-1455">
          Visibility &amp; Status
        </h3>
        <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-4" data-id="element-1456">
          <div className="flex items-center justify-between" data-id="element-1457">
            <div data-id="element-1458">
              <div className="font-medium text-dark-navy flex items-center gap-2" data-id="element-1459">
                Published Status
                <span className={`px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide rounded-md ${published ? 'bg-green-50 text-green-700' : 'bg-slate-100 text-slate-500'}`} data-id="element-1460">
                  {published ? 'Live' : 'Unpublished'}
                </span>
              </div>
              <div className="text-sm text-slate-500" data-id="element-1461">
                Make this item visible to customers.
              </div>
            </div>
            <button onClick={handlePublishToggle} title="Toggle published" data-id="element-1462">
              <ToggleRightIcon className={`w-10 h-10 transition-colors ${published ? 'text-green-500' : 'text-slate-300 rotate-180'}`} data-id="element-1463" />
            </button>
          </div>

          {showTrialPrompt && !trialActive && <div className="mt-4 p-4 bg-indigo-50 border border-indigo-100 rounded-xl" data-id="element-1464">
              <div className="flex items-start gap-3" data-id="element-1465">
                <SparklesIcon className="w-5 h-5 text-primary-indigo flex-shrink-0 mt-0.5" data-id="element-1466" />
                <div data-id="element-1467">
                  <h4 className="font-bold text-indigo-900 mb-1" data-id="element-1468">
                    Activate your 7-day free trial
                  </h4>
                  <p className="text-sm text-indigo-800 mb-3" data-id="element-1469">
                    Start your free trial to publish inventory and start
                    receiving qualified leads.
                  </p>
                  <button onClick={() => {
                startTrial();
                setShowTrialPrompt(false);
                setPublished(true);
                notify('Trial started — item published.');
              }} className="px-4 py-2 bg-primary-indigo text-white text-sm font-medium rounded-lg hover:bg-indigo-700 transition-colors" data-id="element-1470">
                    Start Free Trial
                  </button>
                </div>
              </div>
            </div>}

          <AnimatePresence initial={false} data-id="element-1471">
            {showUnpublish && published && <motion.div initial={{
            opacity: 0,
            height: 0
          }} animate={{
            opacity: 1,
            height: 'auto'
          }} exit={{
            opacity: 0,
            height: 0
          }} className="overflow-hidden" data-id="element-1472">
                <div className="pl-4 border-l-2 border-slate-100 space-y-3" data-id="element-1473">
                  <label className="block text-sm font-medium text-slate-700" data-id="element-1474">
                    Reason for unpublishing
                  </label>
                  <select value={unpublishReason} onChange={e => setUnpublishReason(e.target.value)} className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg focus:ring-2 focus:ring-primary-indigo/20 text-sm" data-id="element-1475">
                    <option value="sold" data-id="element-1476">Sold</option>
                    <option value="not_for_sale" data-id="element-1477">Not for sale anymore</option>
                    <option value="other" data-id="element-1478">Other</option>
                  </select>
                  {unpublishReason === 'other' && <input type="text" value={otherReason} onChange={e => setOtherReason(e.target.value)} placeholder="Please specify..." className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg focus:ring-2 focus:ring-primary-indigo/20 text-sm" data-id="element-1479" />}
                  <div className="flex gap-2" data-id="element-1480">
                    <button onClick={confirmUnpublish} className="px-4 py-2 bg-slate-900 text-white text-sm font-medium rounded-lg hover:bg-slate-800 transition-colors" data-id="element-1481">
                      Confirm Unpublish
                    </button>
                    <button onClick={() => setShowUnpublish(false)} className="px-4 py-2 text-slate-600 text-sm font-medium rounded-lg hover:bg-slate-100 transition-colors" data-id="element-1482">
                      Cancel
                    </button>
                  </div>
                </div>
              </motion.div>}
          </AnimatePresence>

          <div className="h-px bg-slate-100" data-id="element-1483" />
          <div className="flex items-center justify-between" data-id="element-1484">
            <div data-id="element-1485">
              <div className="font-medium text-dark-navy" data-id="element-1486">Hide from Search</div>
              <div className="text-sm text-slate-500" data-id="element-1487">
                Item can only be accessed via direct link.
              </div>
            </div>
            <button onClick={toggleHide} title="Toggle hide from search" data-id="element-1488">
              <ToggleRightIcon className={`w-10 h-10 transition-colors ${hideFromSearch ? 'text-primary-indigo' : 'text-slate-300 rotate-180'}`} data-id="element-1489" />
            </button>
          </div>
        </div>
      </section>

      <section data-id="element-1490">
        <h3 className="text-lg font-bold text-dark-navy mb-4" data-id="element-1491">Danger Zone</h3>
        <div className="bg-red-50 border border-red-100 rounded-xl p-4 space-y-4" data-id="element-1492">
          <div className="flex items-center justify-between" data-id="element-1493">
            <div data-id="element-1494">
              <div className="font-medium text-red-900" data-id="element-1495">
                {archived ? 'Restore Item' : 'Archive Item'}
              </div>
              <div className="text-sm text-red-700" data-id="element-1496">
                {archived ? 'Bring this item back into your active catalog.' : 'Remove from active catalog but keep data.'}
              </div>
            </div>
            <button onClick={toggleArchive} className="px-4 py-2 bg-white border border-red-200 text-red-700 font-medium rounded-lg hover:bg-red-100 flex items-center gap-2 transition-colors" data-id="element-1497">
              <ArchiveIcon className="w-4 h-4" data-id="element-1498" />{' '}
              {archived ? 'Restore' : 'Archive'}
            </button>
          </div>
          <div className="h-px bg-red-100" data-id="element-1499" />
          <div className="flex items-center justify-between" data-id="element-1500">
            <div data-id="element-1501">
              <div className="font-medium text-red-900" data-id="element-1502">Delete Item</div>
              <div className="text-sm text-red-700" data-id="element-1503">
                Permanently remove this item and all its data.
              </div>
            </div>
            {confirmDelete ? <div className="flex items-center gap-2" data-id="element-1504">
                <button onClick={() => setConfirmDelete(false)} className="px-3 py-2 text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors" data-id="element-1505">
                  Cancel
                </button>
                <button onClick={onClose} className="px-4 py-2 bg-red-600 text-white font-medium rounded-lg hover:bg-red-700 flex items-center gap-2 transition-colors" data-id="element-1506">
                  <TrashIcon className="w-4 h-4" data-id="element-1507" /> Confirm Delete
                </button>
              </div> : <button onClick={() => setConfirmDelete(true)} className="px-4 py-2 bg-red-600 text-white font-medium rounded-lg hover:bg-red-700 flex items-center gap-2 transition-colors" data-id="element-1508">
                <TrashIcon className="w-4 h-4" data-id="element-1509" /> Delete
              </button>}
          </div>
        </div>
      </section>
    </div>;
}
interface KnowledgeRule {
  id: number;
  type: string;
  cond: string;
  charge: string;
  active: boolean;
}
interface KnowledgeFaq {
  id: number;
  q: string;
  a: string;
  approved: boolean;
}
function AIRulesTab() {
  const [rules, setRules] = useState<KnowledgeRule[]>([{
    id: 1,
    type: 'Delivery Rule',
    cond: 'Dubai delivery',
    charge: 'AED 50',
    active: true
  }, {
    id: 2,
    type: 'Pickup Rule',
    cond: 'Dubai pickup',
    charge: 'AED 50',
    active: true
  }, {
    id: 3,
    type: 'Airport Rule',
    cond: 'Airport delivery',
    charge: 'AED 75',
    active: true
  }, {
    id: 4,
    type: 'Deposit Rule',
    cond: 'Security deposit',
    charge: 'AED 1,000',
    active: true
  }, {
    id: 5,
    type: 'Restriction',
    cond: 'Minimum rental period',
    charge: '2 days',
    active: false
  }]);
  const [draft, setDraft] = useState('');
  const suggestions = ['Free delivery for rentals over 7 days', 'Add AED 100 for Sharjah delivery', 'No smoking inside the vehicle', 'Weekend rate +15%'];
  // Parse a natural-language rule into a structured row. Pulls out any
  // "AED <amount>" or "<n> day(s)" as the charge, classifies the type by
  // keyword, and uses the full text as the condition.
  const buildRule = (text: string): KnowledgeRule => {
    const trimmed = text.trim();
    const chargeMatch = trimmed.match(/AED\s?[\d,]+/i);
    const durationMatch = trimmed.match(/\d+\s?days?/i);
    const charge = chargeMatch ? chargeMatch[0].toUpperCase().replace('AED', 'AED ').replace(/\s+/g, ' ').trim() : durationMatch ? durationMatch[0] : '—';
    const lower = trimmed.toLowerCase();
    let type = 'Custom Rule';
    if (lower.includes('deliver')) type = 'Delivery Rule';else if (lower.includes('pickup') || lower.includes('pick up')) type = 'Pickup Rule';else if (lower.includes('airport')) type = 'Airport Rule';else if (lower.includes('deposit')) type = 'Deposit Rule';else if (lower.includes('no ') || lower.includes('not ') || lower.includes('minimum') || lower.includes('restrict')) type = 'Restriction';
    return {
      id: Date.now(),
      type,
      cond: trimmed,
      charge,
      active: true
    };
  };
  const addRule = (text?: string) => {
    const value = (text ?? draft).trim();
    if (!value) return;
    setRules(prev => [buildRule(value), ...prev]);
    if (!text) setDraft('');
  };
  const toggleRule = (id: number) => setRules(prev => prev.map(r => r.id === id ? {
    ...r,
    active: !r.active
  } : r));
  const deleteRule = (id: number) => setRules(prev => prev.filter(r => r.id !== id));
  const activeCount = rules.filter(r => r.active).length;
  // ---- FAQs ----
  const [faqs, setFaqs] = useState<KnowledgeFaq[]>([{
    id: 101,
    q: 'What is the minimum rental period?',
    a: 'The minimum rental period for this vehicle is 2 days.',
    approved: true
  }, {
    id: 102,
    q: 'Do you offer delivery to the airport?',
    a: 'Yes, airport delivery is available for an additional charge of AED 75.',
    approved: true
  }, {
    id: 103,
    q: 'Is insurance included in the price?',
    a: 'Basic comprehensive insurance is included. Full coverage is available as an add-on.',
    approved: false
  }, {
    id: 104,
    q: 'What documents are required to rent?',
    a: 'You will need your Emirates ID (or Passport with Visa) and a credit card for the security deposit.',
    approved: true
  }]);
  const [adding, setAdding] = useState(false);
  const [draftQ, setDraftQ] = useState('');
  const [draftA, setDraftA] = useState('');
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editQ, setEditQ] = useState('');
  const [editA, setEditA] = useState('');
  const addFaq = () => {
    if (!draftQ.trim() || !draftA.trim()) return;
    setFaqs(prev => [{
      id: Date.now(),
      q: draftQ.trim(),
      a: draftA.trim(),
      approved: true
    }, ...prev]);
    setDraftQ('');
    setDraftA('');
    setAdding(false);
  };
  const deleteFaq = (id: number) => setFaqs(prev => prev.filter(f => f.id !== id));
  const toggleApprove = (id: number) => setFaqs(prev => prev.map(f => f.id === id ? {
    ...f,
    approved: !f.approved
  } : f));
  const startEdit = (f: KnowledgeFaq) => {
    setEditingId(f.id);
    setEditQ(f.q);
    setEditA(f.a);
  };
  const saveEdit = () => {
    setFaqs(prev => prev.map(f => f.id === editingId ? {
      ...f,
      q: editQ.trim(),
      a: editA.trim()
    } : f));
    setEditingId(null);
  };
  const approvedCount = faqs.filter(f => f.approved).length;
  return <div className="flex flex-col h-full max-w-4xl" data-id="element-1510">
      <div className="mb-5" data-id="element-1511">
        <h3 className="text-lg font-bold text-dark-navy mb-2" data-id="element-1512">AI Knowledge</h3>
        <p className="text-sm text-slate-500" data-id="element-1513">
          Everything Talab needs to answer buyers about this item — pricing
          rules and frequently asked questions, in one place.
        </p>
      </div>

      <div className="flex-1 overflow-y-auto -mr-2 pr-2 space-y-10" data-id="element-1514">
        {/* ============ SECTION 1: PRICING & RULES ============ */}
        <section data-id="element-1515">
          <div className="flex items-center justify-between mb-4" data-id="element-1516">
            <div className="flex items-center gap-2.5" data-id="element-1517">
              <div className="w-8 h-8 rounded-lg bg-ai-purple/10 flex items-center justify-center" data-id="element-1518">
                <SparklesIcon className="w-4 h-4 text-ai-purple" data-id="element-1519" />
              </div>
              <div data-id="element-1520">
                <h4 className="font-bold text-dark-navy leading-tight" data-id="element-1521">
                  Pricing &amp; Rules
                </h4>
                <p className="text-xs text-slate-500" data-id="element-1522">
                  {activeCount} of {rules.length} rules active
                </p>
              </div>
            </div>
          </div>

          {/* Natural Language Input */}
          <div className="mb-4" data-id="element-1523">
            <div className="relative" data-id="element-1524">
              <div className="absolute top-3 left-3 text-ai-purple" data-id="element-1525">
                <SparklesIcon className="w-5 h-5" data-id="element-1526" />
              </div>
              <textarea rows={2} value={draft} onChange={e => setDraft(e.target.value)} onKeyDown={e => {
              if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                e.preventDefault();
                addRule();
              }
            }} placeholder='E.g. "If customer wants delivery to Sharjah, add AED 100."' className="w-full pl-10 pr-28 py-3 bg-purple-50/50 border border-purple-100 rounded-xl focus:outline-none focus:ring-2 focus:ring-ai-purple/30 focus:border-ai-purple text-sm resize-none" data-id="element-1527" />
              <button onClick={() => addRule()} disabled={!draft.trim()} className="absolute right-2 bottom-2 px-3 py-1.5 bg-ai-purple text-white text-xs font-bold rounded-lg hover:bg-purple-600 transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1" data-id="element-1528">
                <PlusIcon className="w-3.5 h-3.5" data-id="element-1529" /> Add Rule
              </button>
            </div>
            <div className="flex flex-wrap gap-2 mt-3" data-id="element-1530">
              {suggestions.map(s => <button key={s} onClick={() => addRule(s)} className="px-3 py-1.5 bg-white border border-slate-200 text-slate-600 text-xs font-medium rounded-full hover:border-ai-purple hover:text-ai-purple hover:bg-purple-50/50 transition-colors flex items-center gap-1" data-id="element-1531">
                  <PlusIcon className="w-3 h-3" data-id="element-1532" /> {s}
                </button>)}
            </div>
          </div>

          {/* Structured Rules Table */}
          <div className="border border-slate-200 rounded-xl overflow-hidden" data-id="element-1533">
            <table className="w-full text-left text-sm" data-id="element-1534">
              <thead className="bg-slate-50 border-b border-slate-200" data-id="element-1535">
                <tr data-id="element-1536">
                  <th className="px-4 py-3 font-medium text-slate-600" data-id="element-1537">
                    Rule Type
                  </th>
                  <th className="px-4 py-3 font-medium text-slate-600" data-id="element-1538">
                    Condition
                  </th>
                  <th className="px-4 py-3 font-medium text-slate-600" data-id="element-1539">
                    Charge
                  </th>
                  <th className="px-4 py-3 font-medium text-slate-600" data-id="element-1540">
                    Status
                  </th>
                  <th className="px-4 py-3 font-medium text-slate-600 text-right" data-id="element-1541">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100" data-id="element-1542">
                <AnimatePresence initial={false} data-id="element-1543">
                  {rules.map(rule => <motion.tr key={rule.id} layout initial={{
                  opacity: 0,
                  backgroundColor: 'rgba(168,85,247,0.06)'
                }} animate={{
                  opacity: 1,
                  backgroundColor: 'rgba(255,255,255,0)'
                }} exit={{
                  opacity: 0,
                  height: 0
                }} className={`hover:bg-slate-50 transition-colors group ${rule.active ? '' : 'opacity-60'}`} data-id="element-1544">
                      <td className="px-4 py-3 font-medium text-dark-navy whitespace-nowrap" data-id="element-1545">
                        {rule.type}
                      </td>
                      <td className="px-4 py-3 text-slate-600 max-w-xs" data-id="element-1546">
                        {rule.cond}
                      </td>
                      <td className="px-4 py-3 text-slate-900 font-medium whitespace-nowrap" data-id="element-1547">
                        {rule.charge}
                      </td>
                      <td className="px-4 py-3" data-id="element-1548">
                        <button onClick={() => toggleRule(rule.id)} title="Toggle status" className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-md transition-colors ${rule.active ? 'bg-green-50 text-green-700 hover:bg-green-100' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'}`} data-id="element-1549">
                          {rule.active ? <>
                              <CheckCircle2Icon className="w-3 h-3" data-id="element-1550" /> Active
                            </> : <>
                              <ClockIcon className="w-3 h-3" data-id="element-1551" /> Paused
                            </>}
                        </button>
                      </td>
                      <td className="px-4 py-3" data-id="element-1552">
                        <div className="flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-1553">
                          <button onClick={() => toggleRule(rule.id)} title={rule.active ? 'Pause rule' : 'Activate rule'} className="p-1.5 text-slate-400 hover:text-primary-indigo hover:bg-indigo-50 rounded-md transition-colors" data-id="element-1554">
                            <ToggleRightIcon className="w-4 h-4" data-id="element-1555" />
                          </button>
                          <button onClick={() => deleteRule(rule.id)} title="Delete rule" className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors" data-id="element-1556">
                            <TrashIcon className="w-4 h-4" data-id="element-1557" />
                          </button>
                        </div>
                      </td>
                    </motion.tr>)}
                </AnimatePresence>
                {rules.length === 0 && <tr data-id="element-1558">
                    <td colSpan={5} className="px-4 py-12 text-center text-sm text-slate-400" data-id="element-1559">
                      No rules yet. Add one above to teach the AI how to handle
                      this item.
                    </td>
                  </tr>}
              </tbody>
            </table>
          </div>
        </section>

        {/* ============ SECTION 2: FAQs ============ */}
        <section data-id="element-1560">
          <div className="flex items-center justify-between mb-4" data-id="element-1561">
            <div className="flex items-center gap-2.5" data-id="element-1562">
              <div className="w-8 h-8 rounded-lg bg-primary-indigo/10 flex items-center justify-center" data-id="element-1563">
                <MessageSquareIcon className="w-4 h-4 text-primary-indigo" data-id="element-1564" />
              </div>
              <div data-id="element-1565">
                <h4 className="font-bold text-dark-navy leading-tight" data-id="element-1566">
                  Frequently Asked Questions
                </h4>
                <p className="text-xs text-slate-500" data-id="element-1567">
                  {approvedCount} of {faqs.length} approved for AI replies
                </p>
              </div>
            </div>
            <button onClick={() => setAdding(v => !v)} className="px-3.5 py-2 bg-primary-indigo/10 text-primary-indigo text-sm font-semibold rounded-lg hover:bg-primary-indigo/20 transition-colors flex items-center gap-1.5" data-id="element-1568">
              <PlusIcon className="w-4 h-4" data-id="element-1569" /> Add FAQ
            </button>
          </div>

          {/* Add FAQ form */}
          <AnimatePresence initial={false} data-id="element-1570">
            {adding && <motion.div initial={{
            opacity: 0,
            height: 0
          }} animate={{
            opacity: 1,
            height: 'auto'
          }} exit={{
            opacity: 0,
            height: 0
          }} className="overflow-hidden" data-id="element-1571">
                <div className="p-4 mb-4 bg-slate-50 border border-slate-200 rounded-xl space-y-3" data-id="element-1572">
                  <input type="text" value={draftQ} onChange={e => setDraftQ(e.target.value)} placeholder="Question — e.g. Can I extend my rental?" className="w-full px-3 py-2.5 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-indigo/20 focus:border-primary-indigo transition-all" data-id="element-1573" />
                  <textarea rows={2} value={draftA} onChange={e => setDraftA(e.target.value)} placeholder="Answer the AI should give…" className="w-full px-3 py-2.5 bg-white border border-slate-200 rounded-lg text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary-indigo/20 focus:border-primary-indigo transition-all" data-id="element-1574" />
                  <div className="flex justify-end gap-2" data-id="element-1575">
                    <button onClick={() => {
                  setAdding(false);
                  setDraftQ('');
                  setDraftA('');
                }} className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-900 rounded-lg transition-colors" data-id="element-1576">
                      Cancel
                    </button>
                    <button onClick={addFaq} disabled={!draftQ.trim() || !draftA.trim()} className="px-4 py-2 bg-primary-indigo text-white text-sm font-semibold rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed" data-id="element-1577">
                      Save FAQ
                    </button>
                  </div>
                </div>
              </motion.div>}
          </AnimatePresence>

          {/* FAQ list */}
          <div className="space-y-3" data-id="element-1578">
            <AnimatePresence initial={false} data-id="element-1579">
              {faqs.map(faq => <motion.div key={faq.id} layout initial={{
              opacity: 0,
              y: 8
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              height: 0
            }} className="p-4 border border-slate-200 rounded-xl hover:border-slate-300 hover:shadow-sm transition-all group" data-id="element-1580">
                  {editingId === faq.id ? <div className="space-y-3" data-id="element-1581">
                      <input type="text" value={editQ} onChange={e => setEditQ(e.target.value)} className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm font-medium focus:outline-none focus:ring-2 focus:ring-primary-indigo/20 focus:border-primary-indigo" data-id="element-1582" />
                      <textarea rows={2} value={editA} onChange={e => setEditA(e.target.value)} className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary-indigo/20 focus:border-primary-indigo" data-id="element-1583" />
                      <div className="flex justify-end gap-2" data-id="element-1584">
                        <button onClick={() => setEditingId(null)} className="px-3 py-1.5 text-sm font-medium text-slate-600 hover:text-slate-900 rounded-lg transition-colors" data-id="element-1585">
                          Cancel
                        </button>
                        <button onClick={saveEdit} className="px-3 py-1.5 bg-primary-indigo text-white text-sm font-semibold rounded-lg hover:bg-indigo-700 transition-colors flex items-center gap-1.5" data-id="element-1586">
                          <CheckIcon className="w-3.5 h-3.5" data-id="element-1587" /> Save
                        </button>
                      </div>
                    </div> : <div className="flex items-start justify-between gap-4" data-id="element-1588">
                      <div className="flex-1 min-w-0" data-id="element-1589">
                        <div className="flex items-center gap-2 mb-2" data-id="element-1590">
                          <h4 className="font-bold text-dark-navy flex items-center gap-2" data-id="element-1591">
                            <span className="text-slate-400" data-id="element-1592">Q:</span> {faq.q}
                          </h4>
                          {!faq.approved && <span className="px-2 py-0.5 bg-amber-50 text-amber-600 text-[10px] font-bold uppercase tracking-wide rounded-md" data-id="element-1593">
                              Draft
                            </span>}
                        </div>
                        <p className="text-sm text-slate-600 flex items-start gap-2" data-id="element-1594">
                          <span className="text-ai-purple font-bold mt-0.5" data-id="element-1595">
                            A:
                          </span>{' '}
                          {faq.a}
                        </p>
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0" data-id="element-1596">
                        <button onClick={() => toggleApprove(faq.id)} title={faq.approved ? 'Approved — click to unapprove' : 'Approve for AI'} className={`p-1.5 rounded-md transition-colors ${faq.approved ? 'text-green-600 bg-green-50 hover:bg-green-100' : 'text-slate-400 hover:text-green-600 hover:bg-green-50'}`} data-id="element-1597">
                          <CheckCircle2Icon className="w-4 h-4" data-id="element-1598" />
                        </button>
                        <button onClick={() => startEdit(faq)} className="p-1.5 text-slate-400 hover:text-primary-indigo hover:bg-indigo-50 rounded-md transition-colors" title="Edit" data-id="element-1599">
                          <EditIcon className="w-4 h-4" data-id="element-1600" />
                        </button>
                        <button onClick={() => deleteFaq(faq.id)} className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors" title="Delete" data-id="element-1601">
                          <TrashIcon className="w-4 h-4" data-id="element-1602" />
                        </button>
                      </div>
                    </div>}
                </motion.div>)}
            </AnimatePresence>
            {faqs.length === 0 && <div className="px-4 py-10 text-center text-sm text-slate-400 border border-dashed border-slate-200 rounded-xl" data-id="element-1603">
                No FAQs yet. Add one so the AI can answer common questions
                instantly.
              </div>}
          </div>
        </section>
      </div>
    </div>;
}